<template>
  <div
    v-if="!loadingMain"
    class="flex flex-col courierAddress"
    :class="{
      'pb-6 lg1024:pb-6': getCountryCode === 'US',
      'lg1024:mt-8 mt-5': true,
    }"
    @click="editScenario"
  >
    <!-- ────────── Заголовок ────────── -->
    <h3 class="text-lg font-medium pb-3 lg1024:text-2xl">
      {{ $t('enter_destination_address') }}
    </h3>

    <!-- ────────── Переключатели карты / скрытых полей ────────── -->
    <template v-if="!hideGmapAutocomplete">
      <!-- показать карту -->
      <a
        analytics-id="analytics-158"
        v-show="!showMap && !showHiddenFields"
        class="font-medium cursor-pointer flex gap-1 w-min whitespace-nowrap text-[#E03B00]"
        @click="emit('update:showMap', true)"
        data-qa="step4-courier-show-map-btn"
      >
        <img src="@/assets/img/map-icon.svg" alt="" />
        <span>{{ $t('unauth_showMap') }}</span>
      </a>
      <!-- скрыть карту -->
      <div
        v-show="showMap"
        class="font-medium cursor-pointer flex gap-1 pb-4 w-min whitespace-nowrap text-[#E03B00]"
        @click="hideMapAndSetZoom"
        data-qa="step4-courier-hide-map-btn"
      >
        <img src="@/assets/img/close-map.svg" alt="" />
        <span>{{ $t('unauth_hideMap') }}</span>
      </div>
      <!-- скрыть ручные поля -->
      <div
        v-show="showHiddenFields"
        class="font-medium cursor-pointer flex gap-1 w-min whitespace-nowrap text-[#E03B00]"
        @click="emit('hideHiddenFields')"
        data-qa="step4-courier-hide-hidden-btn-top"
      >
        <img
          src="@/assets/img/close-map.svg"
          alt=""
          class="w-[24px] h-[24px]"
        />
        <span>{{ $t('checkout_hide_manual_address') }}</span>
      </div>
    </template>

    <!-- ────────── Карта ────────── -->
    <GoogleMap
      v-if="!loadingMap && showMap"
      :styles="$isMobile ? 'width: 100%; height: 90vw;' : 'width: 100%'"
      :initial-markers="markers"
      :initial-zoom="initialZoom"
      :zoom-to-cords="zoomToCordsObj"
      :center-map-to-cords="centerCoords"
      :need-default-zoom="needDefaultZoom"
      :map-options="mapOptions"
      get-address-on-click
      :city="addressInfo?.PROFILE?.CITY"
      @get-address-info="formAddressField"
    />

    <!-- ────────── Предупреждения ────────── -->
    <div
      v-if="kyivOutside || spbOutside"
      class="redHolder px-4 pt-2 pb-1 mt-8 flex justify-start basket-row"
    >
      <ExclamationAlertIcon color="#ff585d" topPosition="2" class="mr-1" />
      <span class="txt text-xs">{{ $t('kyiv_outside') }}</span>
    </div>
    <div
      v-if="moscowOutside"
      class="redHolder px-4 pt-2 pb-1 mt-8 flex justify-start basket-row"
    >
      <ExclamationAlertIcon color="#ff585d" topPosition="2" class="mr-1" />
      <span class="txt text-xs">{{ $t('msk_courier_1day_restriction') }}</span>
    </div>

    <div :class="{ 'address-field-locked': addressFieldLocked }">
      <!-- ────────── Autocomplete ────────── -->
      <div
        v-if="!hideGmapAutocomplete && !showHiddenFields"
        class="flex flex-col mt-4"
      >
        <div class="flex">
          <div
            class="afterTitle autocomplete-label"
            :class="{ active: addressPopup }"
            v-click-outside="closePopup"
          >
            <label for="courier-address-field" class="flex">
              {{
                englishAddress
                  ? $t('ORDER_FIELD_AUTOCOMPLETE_US')
                  : $t('delivery_address_label')
              }}
              <span class="required">*</span>
            </label>
          </div>
        </div>
        <div
          :class="[
            'autocomplete-form',
            {
              'autocomplete-form-error': courierAddressFieldError,
            },
          ]"
        >
          <GMapAutocomplete
            id="courier-address-field"
            ref="courierAddress"
            class="border border-gray-2 p-3 pl-12 w-full mt-1 input-wrap autocomplete-form-input"
            data-qa="step4-courier-autocomplete"
            :placeholder="$t('shipping_address_mask')"
            :bounds="positionData.circle"
            strict-bounds
            :component-restrictions="{ country: countryRestriction }"
            :value="addressInfo.formatted"
            :fields="[
              'address_components',
              'formatted_address',
              'geometry',
              'place_id',
            ]"
            @place_changed="formAddressField"
            @keyup="formAddressField(false, $event)"
            @focusout="
              !showHiddenFields &&
              validateAddressField($event.target.value, true)
            "
            @input="incrementAddressEditCounter(getCheckoutContext(store))"
          />
          <img
            src="@/assets/img/search-md.svg"
            alt=""
            class="autocomplete-form-icon"
            @click="focusAutocomplete"
          />
        </div>
      </div>

      <!-- ────────── Ручные поля адреса ────────── -->
      <div
        v-if="fieldsConfig?.ADDRESS_INFO_FIELDS && addressInfo?.PROFILE"
        class="mt-4 w-full grid grid-cols-1 lg1024:grid-cols-4 gap-4"
      >
        <template
          v-for="field in fieldsConfig.ADDRESS_INFO_FIELDS"
          :key="field.name"
        >
          <div
            v-if="
              (field.type === 'text' || field.type === 'number') &&
              getFieldProp('ADDRESS_INFO_FIELDS', field.name, 'label')
            "
            v-show="
              field.visual ||
              (showHiddenFields &&
                !['FIO', 'EMAIL', 'PHONE', 'COUNTRY', 'CITY', 'AREA'].includes(
                  field.name
                ))
            "
            :class="{
              'lg1024:col-span-4 col-span-2 order-first max-lg:col-span-1':
                (hideGmapAutocomplete || showHiddenFields) &&
                field.name.toLowerCase() === 'street',
              'lg1024:col-span-4':
                field.name.toLowerCase() === 'apartment' && englishAddress,
            }"
            class="flex flex-col justify-start"
          >
            <label :for="field.name.toLowerCase()" class="flex">
              {{ getFieldProp('ADDRESS_INFO_FIELDS', field.name, 'label') }}
              <span
                v-if="
                  getFieldProp('ADDRESS_INFO_FIELDS', field.name, 'required')
                "
                class="required"
                >*</span
              >
            </label>
            <input
              :id="field.name.toLowerCase()"
              class="border border-gray-2 p-3 mt-1 input-wrap"
              :data-qa="'step4-courier-input-' + field.name.toLowerCase()"
              autocomplete="off"
              :disabled="
                field.name.toLowerCase() !== 'apartment' &&
                getCountryCode === 'US' &&
                !showHiddenFields
              "
              :minlength="field.min"
              :maxlength="field.max ? field.max + 1 : null"
              :type="field.type === 'number' ? 'text' : field.type"
              :inputmode="field.type === 'number' ? 'numeric' : null"
              v-model="addressInfo.PROFILE[field.name]"
              :class="{
                'has-error': addressFieldErrors[field.name]?.length,
              }"
              @input="handleMaxLength($event, field)"
              @keyup="
                validateField(
                  'ADDRESS_INFO_FIELDS',
                  field.name,
                  $event.target.value
                )
              "
              @focusout="handleFieldFocusOut(field, $event.target.value)"
              @change="emit('manualAddressChange', field.name)"
            />
            <p
              v-if="errorMessages[field.name]"
              class="text-orange-500 text-xs leading-none mt-1"
            >
              {{ errorMessages[field.name] }}
            </p>
          </div>
        </template>
      </div>
    </div>
    <p
      v-if="courierAddressFieldError"
      class="mb-2 mt-2 text-sm text-accentColor"
    >
      {{ courierAddressFieldError }}.&nbsp;
    </p>
    <!-- ────────── Кнопка «показать скрытые поля» ────────── -->
    <div
      v-if="
        !(
          store.state.addressInfo.PROFILE.CITY === 'Москва' &&
          store.state.selectedDeliveryTypeService === 'I_COURIER_1DAY'
        ) && !showHiddenFields
      "
      class="my-2 flex flex-col"
    >
      <span
        v-if="!hideGmapAutocomplete"
        id="showHiddenFields"
        class="text-[#333F48] text-[16px]"
      >
        {{ $t('show_hidden_fields') }}
      </span>

      <a analytics-id="analytics-159">
        <div
          v-if="!hideGmapAutocomplete"
          class="flex gap-1 mt-4 cursor-pointer w-min whitespace-nowrap"
          @click="emit('showHiddenFields')"
          data-qa="step4-courier-show-hidden-btn"
        >
          <img src="@/assets/img/edit-icon.svg" alt="" />
          <p class="text-[16px] text-[#E03B00] font-medium">
            {{ $t('show_hidden_fields_button') }}
          </p>
        </div>
      </a>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import GoogleMap from '@/components/address/Map.vue'
import { useAddressFields } from '../composables/useAddressFields'
import {
  getCheckoutContext,
  incrementAddressEditCounter,
} from '@/utils/sentryEvents'
import ExclamationAlertIcon from '@/components/svg/ExclamationAlertIcon.vue'

/* Props */
const props = defineProps({
  modelValue: { type: Object, required: true }, // addressInfo с formatted + PROFILE
  addressFieldLocked: Boolean,
  loadingMain: Boolean,
  loadingMap: Boolean,
  showMap: Boolean,
  showHiddenFields: Boolean,
  hideGmapAutocomplete: Boolean,

  /* geo & map */
  positionData: Object,
  countryRestriction: [String, Array],
  getCountryCode: String,
  courierAddressMarker: Object,
  zoomToCordsObj: Object,
  googleObject: Object,
  mapOptions: Object,
  needDefaultZoom: Boolean,

  /* validation helpers */
  getFieldProp: Function,
  validateField: Function,
  handleMaxLength: Function,
  onAddressFieldFocusOut: Function,
  formAddressField: Function,
  validateAddressField: Function,
  focusAutocomplete: Function,
  closePopup: Function,

  /* misc */
  errorMessages: Object,
  fieldsConfig: Object,
  englishAddress: Boolean,
  addressPopup: Boolean,
  kyivOutside: Boolean,
  spbOutside: Boolean,
  moscowOutside: Boolean,

  profileAddressComment: String,
})

const emit = defineEmits([
  'update:modelValue',
  'update:profileAddressComment',
  'update:showMap',
  'showHiddenFields',
  'hideHiddenFields',
  'manualAddressChange',
])

// приоритет: вручную введённые данные → профиль по умолчанию
const { addressInfo } = useAddressFields(props, emit, 'courier_address')

const store = useStore()
const courierAddressFieldError = computed(
  () => store.getters.courierAddressFieldError
)
const addressFieldErrors = computed(() => store.getters.addressFieldErrors)

function handleFieldFocusOut(field, value) {
  props.validateField('ADDRESS_INFO_FIELDS', field.name, value)
  props.onAddressFieldFocusOut?.(field.name)
}

function editScenario() {
  store.commit('setCheckoutScenario', 'userEditProfile')
}

/* ===== карта ===== */
const markers = computed(() => [
  {
    position: {
      lat: props.courierAddressMarker.lat,
      lng: props.courierAddressMarker.lng,
    },
  },
])

const centerCoords = computed(() => {
  return props.courierAddressMarker.lat && props.courierAddressMarker.lng
    ? {
        lat: props.courierAddressMarker.lat,
        lng: props.courierAddressMarker.lng,
      }
    : {
        lat: props.googleObject.geometry?.location.lat() || 0,
        lng: props.googleObject.geometry?.location.lng() || 0,
      }
})

const initialZoom = ref(10)
const hideMapAndSetZoom = () => {
  initialZoom.value = 20
  emit('update:showMap', false)
}
watch(
  () => addressInfo.value?.PROFILE?.CITY,
  () => {
    initialZoom.value = 10
  }
)
</script>

<style lang="scss" scoped>
.autocomplete-form-icon {
  @apply absolute right-2 top-3 cursor-pointer;
}

.autocomplete-form-input {
  border-radius: 5px;
  width: 100%;
  padding-left: 40px;
}
.autocomplete-form-input:focus {
  outline: none;
}
.autocomplete-form-input::placeholder {
  color: #8e9aa4;
}
.autocomplete-form-icon {
  position: absolute;
  left: 10px;
  top: 15px;
  cursor: pointer;
}
.autocomplete-form-error {
  padding-bottom: 0;
}
@media (max-width: 1020px) {
  .autocomplete-form {
    max-width: 100%;
  }
}
.autocomplete-form {
  position: relative;
  padding-bottom: 15px;

  &.englishAddress {
    padding-bottom: 0;
  }

  &.autocomplete-form-map {
    padding-bottom: 0;
  }
}

@keyframes breathe {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.address-field-locked {
  filter: blur(2px);
  transform: scale(0.99);
  pointer-events: none;
  animation: breathe 1.5s ease-in-out infinite;
  background-color: rgba(44, 44, 44, 0.05);
}

/* Пользователям с prefers-reduced-motion не показываем блюр/скейл/пульсацию */
@media (prefers-reduced-motion: reduce) {
  .address-field-locked {
    filter: none;
    transform: none;
    animation: none;
  }
}
</style>
